import { Group, Text } from "@mantine/core"
import {
  IconCheck,
  IconHourglass,
  IconX,
  IconCancel,
} from "@tabler/icons-react"
import { SegmentedBadge, Tooltip } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { CertificateRequestDto } from "~/gen/api"
import { useAuth } from "~/store/auth-context"

type Props = {
  requests: CertificateRequestDto[]
}

export const CertificateRequestsSummaryBadge = ({ requests }: Props) => {
  const { t } = useTranslation("certificates")
  const counts = requests.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1
    return acc
  }, {})
  const { isAdmin } = useAuth()

  const STATUS_CONFIG = {
    ACCEPTED: {
      icon: IconCheck,
      color: "green",
      key: t("lists.columns.certificateRequests.validated", {
        count: counts.ACCEPTED ?? 0,
      }),
    },
    REJECTED: {
      icon: IconX,
      color: "red",
      key: t("lists.columns.certificateRequests.rejected", {
        count: counts.REJECTED ?? 0,
      }),
    },
    SUBMITTED: {
      icon: IconHourglass,
      color: "var(--mantine-color-blue-5)",
      key: t("lists.columns.certificateRequests.submitted", {
        count: counts.SUBMITTED ?? 0,
      }),
    },
    ...(isAdmin
      ? {
          CANCELED: {
            icon: IconCancel,
            color: "var(--mantine-color-purple-5)",
            key: t("lists.columns.certificateRequests.canceled", {
              count: counts.CANCELED ?? 0,
            }),
          },
        }
      : {}),
  } as const

  return (
    <SegmentedBadge
      items={Object.entries(STATUS_CONFIG).map(
        ([status, { icon: Icon, color, key }]) => (
          <Tooltip key={status} label={key}>
            <Group gap="var(--mantine-spacing-xxs)" wrap="nowrap">
              <Icon size={12} color={color} />
              <Text fz="xs">{counts[status] ?? 0}</Text>
            </Group>
          </Tooltip>
        )
      )}
    />
  )
}
